import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { off } from "firebase/database";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startListenerAdvertiserTimezone,
  startSaveAdvertiserTimezone,
} from "../actions/user";
import { getBrowserTimezone, isValidTimezone } from "../utils/timezone";

export const AdvertiserTimezoneContext = createContext(null);

export const AdvertiserTimezoneProvider = ({ children }) => {
  const dispatch = useDispatchApp();
  const role = useSelectorApp((state) => state.auth?.user?.role);
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const shopID = useSelectorApp((state) => state.auth?.user?.shopID);
  const [timezone, setTimezone] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const listenerRef = useRef(null);

  useEffect(() => {
    if (!role) {
      setTimezone(null);
      setIsLoaded(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoaded(false);

    const listenPromise = dispatch(
      startListenerAdvertiserTimezone({
        onUpdatedDataCallback: (value) => {
          if (!isMounted) {
            return;
          }
          setTimezone(isValidTimezone(value) ? value : null);
          setIsLoaded(true);
        },
        onReferenceAvailable: (reference) => {
          listenerRef.current = reference;
        },
      })
    );

    Promise.resolve(listenPromise).then((started) => {
      if (!isMounted || started !== false || listenerRef.current) {
        return;
      }
      setTimezone(null);
      setIsLoaded(true);
    });

    return () => {
      isMounted = false;
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, [advertiserID, dispatch, role, shopID]);

  const onSaveTimezone = useCallback(
    async ({ timezone: nextTimezone }) => {
      if (!isValidTimezone(nextTimezone)) {
        return { ok: false };
      }

      setTimezone(nextTimezone);
      return dispatch(startSaveAdvertiserTimezone({ timezone: nextTimezone }));
    },
    [dispatch]
  );

  const displayTimezone = timezone || getBrowserTimezone();

  const value = useMemo(
    () => ({
      timezone,
      displayTimezone,
      isLoaded,
      onSaveTimezone,
    }),
    [displayTimezone, isLoaded, onSaveTimezone, timezone]
  );

  return (
    <AdvertiserTimezoneContext.Provider value={value}>
      {children}
    </AdvertiserTimezoneContext.Provider>
  );
};
